import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Region } from "@typedoc/contracts/regions";
import { normalizeAppError, type AppError } from "./app-error";
import { authClient } from "./auth-client";
import { workspacesQueryOptions } from "./queries/workspaces";
import { activeWorkspace, type WorkspacesResponse } from "./workspaces";

/**
 * Who and where, as far as it is known: the signed-in person, the workspace the session is in and
 * its region. `ready` is false, and the workspace and region empty, until both are known.
 *
 * The workspace comes from a request that can fail, and a failure must not read as loading:
 * `failure` is what to say and `noWorkspace` is a session that is in none. A cancelled request
 * (React's development double mount cancels the first fetch) is not a failure; the retry that
 * follows it answers.
 */
export type WorkspaceScope = {
  userId: string;
  workspaceId: string;
  region: Region | undefined;
  ready: boolean;
  failure: AppError | null;
  noWorkspace: boolean;
};

export function readWorkspaceScope({
  userId,
  data,
  error,
  settling,
}: {
  userId: string;
  data: WorkspacesResponse | undefined;
  error: unknown;
  /**
   * The listing was invalidated, as it is when a switch is acknowledged. Until a fresh answer
   * arrives the cached one still names the workspace the session has just left, so for as long as
   * it is invalidated (while it is read again, and if that read fails) the workspace is unknown, as
   * it is before the first answer: nothing is read or written under it, and a failure is said.
   */
  settling: boolean;
}): WorkspaceScope {
  const workspaceId = settling ? "" : (activeWorkspace(data)?.id ?? "");
  const ready = userId !== "" && workspaceId !== "";
  const failed = !ready && error ? normalizeAppError(error) : null;
  const failure = failed?.code === "REQUEST_CANCELLED" ? null : failed;
  return {
    userId,
    workspaceId,
    region: ready ? data?.organisation.region : undefined,
    ready,
    failure,
    noWorkspace: !ready && !settling && failure === null && data !== undefined,
  };
}

/** `readWorkspaceScope` for the signed-in person, with a way to ask again. */
export function useWorkspaceScope(): WorkspaceScope & { retry: () => void } {
  const { data: session } = authClient.useSession();
  const userId = session?.user.id ?? "";
  const queryClient = useQueryClient();
  const options = workspacesQueryOptions(userId);
  const listing = useQuery({ ...options, enabled: userId !== "" });
  const settling = queryClient.getQueryState(options.queryKey)?.isInvalidated === true;
  const { refetch } = listing;
  const retry = useCallback(() => void refetch(), [refetch]);
  return {
    ...readWorkspaceScope({ userId, data: listing.data, error: listing.error, settling }),
    retry,
  };
}
